/**
 * Weekly EMS cross — the EMS lane (TEMA(low, 8) vs SMA(low, 9), latched) run on
 * ISO-week bars instead of daily. Mirrors the daily EMS latch in `dots.ts` so
 * the two can't drift: +1 once TEMA crosses above SMA (buy), -1 once SMA crosses
 * above TEMA (sell). The "cross" is whether the latched weekly state flipped on
 * the latest (in-progress) weekly bar vs the prior week.
 */
import type { OHLCV } from "../types";
import { temaFull, smaFull } from "./ta";
import { ageSeries } from "./age";
import { resampleOHLC, htIndexPerDay } from "./weekly";

/** Latched EMS state (+1/-1) from a lows series — TEMA(low, temaLen) vs
 *  SMA(low, smaLen). Exact mirror of the daily EMS lane in `dots.ts`: +1 once
 *  TEMA crosses above SMA, -1 once SMA crosses above TEMA; seeded short. */
export function emsStateFromLows(
  lows: ReadonlyArray<number>,
  temaLen = 8,
  smaLen = 9,
): number[] {
  const n = lows.length;
  const tema = temaFull(lows, temaLen);
  const sm = smaFull(lows, smaLen);
  const out = new Array<number>(n).fill(0);
  let buy = false;
  for (let i = 0; i < n; i++) {
    if (i > 0) {
      const buyX = tema[i]! > sm[i]! && tema[i - 1]! <= sm[i - 1]!;
      const sellX = sm[i]! > tema[i]! && sm[i - 1]! <= tema[i - 1]!;
      if (buyX) buy = true;
      else if (sellX) buy = false;
    }
    out[i] = buy ? 1 : -1;
  }
  return out;
}

/** Weekly EMS cross on the latest (in-progress) weekly bar: +1 red→green
 *  up-cross / -1 green→red down-cross / 0 none. Resamples daily OHLC to ISO
 *  weeks (low = the week's minimum), runs the EMS latch on the weekly lows, then
 *  compares the last two weekly states. Null without enough weekly history. */
export function emsWeeklyCross(data: OHLCV, temaLen = 8, smaLen = 9): number | null {
  const { times, opens, highs, lows, closes } = data;
  const n = closes.length;
  if (!times || times.length !== n || !opens || !highs || !lows) return null;
  const wk = resampleOHLC(times, opens, highs, lows, closes, "week");
  if (wk.l.length < 20) return null;
  const st = emsStateFromLows(wk.l, temaLen, smaLen);
  const m = st.length;
  if (m < 2) return null;
  const a = st[m - 1]!, b = st[m - 2]!;
  return a > 0 && b <= 0 ? 1 : a < 0 && b >= 0 ? -1 : 0;
}

/** Per-week EMS cross projected onto daily bars (each day → its week's cross):
 *  +1 red→green / -1 green→red / 0 none. `lastFinite` equals `emsWeeklyCross`. */
export function emsWeeklyCrossSeries(data: OHLCV, temaLen = 8, smaLen = 9): (number | null)[] {
  const { times, opens, highs, lows, closes } = data;
  const n = closes.length;
  if (!times || times.length !== n || !opens || !highs || !lows) return new Array(n).fill(null);
  const wk = resampleOHLC(times, opens, highs, lows, closes, "week");
  if (wk.l.length < 20) return new Array(n).fill(null);
  const st = emsStateFromLows(wk.l, temaLen, smaLen);
  const crossW = st.map((s, k) => {
    if (k === 0) return 0;
    const b = st[k - 1]!;
    return s > 0 && b <= 0 ? 1 : s < 0 && b >= 0 ? -1 : 0;
  });
  const wkIdx = htIndexPerDay(times, wk.t);
  return times.map((_, i) => {
    const k = wkIdx[i]!;
    return k >= 0 && k < crossW.length ? crossW[k]! : null;
  });
}

/** Per-week EMS latched state projected onto daily bars (each day → its week's
 *  Bull/Bear state). lastFinite = the current weekly EMS regime. */
export function emsWeeklyStateSeries(data: OHLCV, temaLen = 8, smaLen = 9): (number | null)[] {
  const { times, opens, highs, lows, closes } = data;
  const n = closes.length;
  if (!times || times.length !== n || !opens || !highs || !lows) return new Array(n).fill(null);
  const wk = resampleOHLC(times, opens, highs, lows, closes, "week");
  if (wk.l.length < 20) return new Array(n).fill(null);
  const st = emsStateFromLows(wk.l, temaLen, smaLen);
  const wkIdx = htIndexPerDay(times, wk.t);
  return times.map((_, i) => { const k = wkIdx[i]!; return k >= 0 && k < st.length ? st[k]! : null; });
}

/** Weeks the weekly EMS state has held, projected onto daily bars. */
export function emsWeeklyStateAgeSeries(data: OHLCV, temaLen = 8, smaLen = 9): (number | null)[] {
  const { times, opens, highs, lows, closes } = data;
  const n = closes.length;
  if (!times || times.length !== n || !opens || !highs || !lows) return new Array(n).fill(null);
  const wk = resampleOHLC(times, opens, highs, lows, closes, "week");
  if (wk.l.length < 20) return new Array(n).fill(null);
  const wAge = ageSeries(emsStateFromLows(wk.l, temaLen, smaLen), "state");
  const wkIdx = htIndexPerDay(times, wk.t);
  return times.map((_, i) => { const k = wkIdx[i]!; return k >= 0 && k < wAge.length ? wAge[k]! : null; });
}

